import { c as _c } from "react/compiler-runtime";
import { parseAsString } from '@repo/sdk/qs/client';
import { Button, Separator } from '@repo/ui/components';
import { X } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTableQueryState } from '../../table/hooks/use-table-query-state.ts';
import type { Column, ColumnDataType, DataTableFilterActions, FilterModel, FiltersState, FilterStrategy } from '../core';
import { type Locale, t } from '../lib/i18n';
import { overdueDays } from '../lib/relative-date';
import { FilterActions } from './filter-actions.tsx';
import { FilterOperator } from './filter-operator.tsx';
import { FilterSubject } from './filter-subject';
import { FilterValue, FilterValueCurrency } from './filter-value';
interface ActiveFilterProps<TData, TType extends ColumnDataType> {
  actions: DataTableFilterActions;
  column: Column<TData, TType>;
  filter: FilterModel<TType>;
  locale?: Locale;
  strategy: FilterStrategy;
}
interface ActiveFiltersProps<TData> {
  actions: DataTableFilterActions;
  columns: Column<TData>[];
  filters: FiltersState;
  locale?: Locale;
  strategy: FilterStrategy;
}
const FilterChip = t0 => {
  const $ = _c(8);
  const {
    children,
    onRemove,
    removeLabel
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Separator orientation="vertical" data-sentry-element="Separator" data-sentry-source-file="active-filters.tsx" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <X className="size-4 -translate-x-0.5" data-sentry-element="X" data-sentry-source-file="active-filters.tsx" />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onRemove || $[3] !== removeLabel) {
    t3 = <Button aria-label={removeLabel} className="h-full w-7 rounded-none rounded-r-2xl text-xs" onClick={onRemove} variant="ghost" data-sentry-element="Button" data-sentry-source-file="active-filters.tsx">{t2}</Button>;
    $[2] = onRemove;
    $[3] = removeLabel;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children || $[6] !== t3) {
    t4 = <div className="flex h-7 items-center rounded-2xl border border-border bg-background text-xs shadow-xs" data-sentry-component="FilterChip" data-sentry-source-file="active-filters.tsx">{children}{t1}{t3}</div>;
    $[5] = children;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
const getRemoveLabel = (name: string, locale: Locale) => t('removeFilter', locale).replace('{name}', name);

// Generic render function for a filter with type-safe value
export function ActiveFilter(t0) {
  const $ = _c(32);
  const {
    actions,
    column,
    filter,
    locale: t1,
    strategy
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  let t2;
  if ($[0] !== actions || $[1] !== filter.columnId) {
    t2 = () => actions.removeFilter(filter.columnId);
    $[0] = actions;
    $[1] = filter.columnId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== column.displayName || $[4] !== locale) {
    t3 = getRemoveLabel(column.displayName, locale);
    $[3] = column.displayName;
    $[4] = locale;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== column) {
    t4 = <FilterSubject column={column} data-sentry-element="FilterSubject" data-sentry-source-file="active-filters.tsx" />;
    $[6] = column;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Separator orientation="vertical" data-sentry-element="Separator" data-sentry-source-file="active-filters.tsx" />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== actions || $[10] !== column || $[11] !== filter || $[12] !== locale) {
    t6 = (column.showOperator || filter.columnId === "status" || column.type === "number" || hasAmount(filter)) && overdueDays(column, filter) === undefined && <><FilterOperator actions={actions} column={column} filter={filter} locale={locale} /><Separator orientation="vertical" /></>;
    $[9] = actions;
    $[10] = column;
    $[11] = filter;
    $[12] = locale;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== actions || $[15] !== column || $[16] !== filter || $[17] !== locale || $[18] !== strategy) {
    t7 = <FilterValue actions={actions} column={column} filter={filter} locale={locale} strategy={strategy} data-sentry-element="FilterValue" data-sentry-source-file="active-filters.tsx" />;
    $[14] = actions;
    $[15] = column;
    $[16] = filter;
    $[17] = locale;
    $[18] = strategy;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== actions || $[21] !== column || $[22] !== filter || $[23] !== locale) {
    t8 = <FilterValueCurrency actions={actions} column={column} filter={filter} locale={locale} data-sentry-element="FilterValueCurrency" data-sentry-source-file="active-filters.tsx" />;
    $[20] = actions;
    $[21] = column;
    $[22] = filter;
    $[23] = locale;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t2 || $[26] !== t3 || $[27] !== t4 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8) {
    t9 = <FilterChip onRemove={t2} removeLabel={t3} data-sentry-element="FilterChip" data-sentry-component="ActiveFilter" data-sentry-source-file="active-filters.tsx">{t4}{t5}{t6}{t7}{t8}</FilterChip>;
    $[25] = t2;
    $[26] = t3;
    $[27] = t4;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  return t9;
}

/** The table's search, shown as a filter so a search saved into a view isn't missed. */
const SearchFilter = t0 => {
  const $ = _c(17);
  const {
    locale,
    onRemove,
    search
  } = t0;
  let T0;
  let name;
  let t1;
  let t2;
  if ($[0] !== locale || $[1] !== onRemove) {
    name = t("searchFilter", locale);
    T0 = FilterChip;
    t1 = onRemove;
    t2 = getRemoveLabel(name, locale);
    $[0] = locale;
    $[1] = onRemove;
    $[2] = T0;
    $[3] = name;
    $[4] = t1;
    $[5] = t2;
  } else {
    T0 = $[2];
    name = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[6] !== name) {
    t3 = <span className="flex items-center px-2 font-medium whitespace-nowrap select-none">{name}</span>;
    $[6] = name;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Separator orientation="vertical" data-sentry-element="Separator" data-sentry-source-file="active-filters.tsx" />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== search) {
    t5 = <span className="max-w-48 truncate px-2" title={search}>“{search}”</span>;
    $[9] = search;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== T0 || $[12] !== t1 || $[13] !== t2 || $[14] !== t3 || $[15] !== t5) {
    t6 = <T0 onRemove={t1} removeLabel={t2} data-sentry-element="FilterChip" data-sentry-component="SearchFilter" data-sentry-source-file="active-filters.tsx">{t3}{t4}{t5}</T0>;
    $[11] = T0;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};
export function ActiveFilters(t0) {
  const $ = _c(32);
  const {
    actions,
    columns,
    filters,
    locale: t1,
    strategy
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      search: parseAsString
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    setValuesAndResetPage,
    values
  } = useTableQueryState(t2);
  const {
    search
  } = values;
  const hasFilters = filters.length > 0 || Boolean(search);
  let t3;
  if ($[1] !== setValuesAndResetPage) {
    t3 = () => void setValuesAndResetPage({
      search: null
    });
    $[1] = setValuesAndResetPage;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const clearSearch = t3;
  const t4 = !hasFilters && "hidden";
  let t5;
  if ($[3] !== t4) {
    t5 = cn("flex items-center gap-2", t4);
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== clearSearch || $[6] !== locale || $[7] !== search) {
    t6 = search ? <SearchFilter locale={locale} onRemove={clearSearch} search={search} /> : null;
    $[5] = clearSearch;
    $[6] = locale;
    $[7] = search;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== actions || $[10] !== columns || $[11] !== filters || $[12] !== locale || $[13] !== strategy) {
    let t8;
    if ($[15] !== actions || $[16] !== columns || $[17] !== locale || $[18] !== strategy) {
      t8 = filter => {
        const id = filter.columnId;
        const column = columns.find(c => c.id === id);
        if (!column) {
          return null;
        }
        if (!filter.values) {
          return null;
        }
        return <ActiveFilter actions={actions} column={column} filter={filter} key={`active-filter-${filter.columnId}`} locale={locale} strategy={strategy} />;
      };
      $[15] = actions;
      $[16] = columns;
      $[17] = locale;
      $[18] = strategy;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    t7 = filters.map(t8);
    $[9] = actions;
    $[10] = columns;
    $[11] = filters;
    $[12] = locale;
    $[13] = strategy;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[20] !== actions || $[21] !== clearSearch) {
    t8 = () => {
      actions.removeAllFilters();
      clearSearch();
    };
    $[20] = actions;
    $[21] = clearSearch;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== hasFilters || $[24] !== locale || $[25] !== t8) {
    t9 = <FilterActions hasFilters={hasFilters} locale={locale} onClear={t8} data-sentry-element="FilterActions" data-sentry-source-file="active-filters.tsx" />;
    $[23] = hasFilters;
    $[24] = locale;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t5 || $[28] !== t6 || $[29] !== t7 || $[30] !== t9) {
    t10 = <div className={t5} data-sentry-component="ActiveFilters" data-sentry-source-file="active-filters.tsx">{t6}{t7}{t9}</div>;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}
export function ActiveFiltersMobileContainer(t0) {
  const $ = _c(16);
  const {
    children
  } = t0;
  const scrollContainerRef = useRef(null);
  const [showLeftBlur, setShowLeftBlur] = useState(false);
  const [showRightBlur, setShowRightBlur] = useState(true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (scrollContainerRef.current) {
        const {
          clientWidth,
          scrollLeft,
          scrollWidth
        } = scrollContainerRef.current;
        setShowLeftBlur(scrollLeft > 0);
        setShowRightBlur(scrollLeft + clientWidth < scrollWidth - 1);
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const checkScroll = t1;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (scrollContainerRef.current) {
        const resizeObserver = new ResizeObserver(() => {
          checkScroll();
        });
        resizeObserver.observe(scrollContainerRef.current);
        return () => {
          resizeObserver.disconnect();
        };
      }
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      checkScroll();
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== children) {
    t5 = [children];
    $[4] = children;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== showLeftBlur) {
    t6 = showLeftBlur && <div className="pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-16 animate-in bg-gradient-to-r from-background to-transparent fade-in-0" />;
    $[6] = showLeftBlur;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== children) {
    t7 = <div className="no-scrollbar flex gap-2 overflow-x-scroll" onScroll={checkScroll} ref={scrollContainerRef}>{children}</div>;
    $[8] = children;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== showRightBlur) {
    t8 = showRightBlur && <div className="pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-16 animate-in bg-gradient-to-l from-background to-transparent fade-in-0" />;
    $[10] = showRightBlur;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t6 || $[13] !== t7 || $[14] !== t8) {
    t9 = <div className="relative w-full overflow-x-hidden" data-sentry-component="ActiveFiltersMobileContainer" data-sentry-source-file="active-filters.tsx">{t6}{t7}{t8}</div>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}

/* A currency-only amount filter has no amount for an operator to apply to. */
function hasAmount(filter: FilterModel): boolean {
  return filter.type === 'amount' && filter.values.some(value => value.amount !== null);
}